import { css } from "@emotion/react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronUp, LoaderCircle, Package } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { type Catalog, useCatalogState } from "../../hooks/useCatalogState";
import type { Product } from "../../hooks/useInventoryState";
import { useProductDetailsState } from "../../hooks/useProductDetailsState";
import { useThemeState } from "../../hooks/useThemeState";
import { useTranslate } from "../../hooks/useTranslate/useTranslate";
import type { ImmutableArray, ImmutableObject } from "../../lib/immutable";
import { ReservationDates } from "../CheckoutForm/ReservationDates";
import { CustomScrollbars } from "../CustomScrollbars/CustomScrollbars";
import { Button } from "../ui/button";
import { DepartmentCategorySubcategoryLinks } from "./DepartmentCategorySubcategoryLinks";
import { AvailabilityField } from "./ItemDetailFields/AvailabilityField";
import { BarcodesField } from "./ItemDetailFields/BarcodesField";
import { DetailedDescriptionField } from "./ItemDetailFields/DetailedDescriptionField";
import { ImageField } from "./ItemDetailFields/ImageField";
import { ItemNameField } from "./ItemDetailFields/ItemNameField";
import { ItemNoField } from "./ItemDetailFields/ItemNoField";
import { ManufacturerField } from "./ItemDetailFields/ManufacturerField";
import { ManufacturerPartNumberField } from "./ItemDetailFields/ManufacturerPartNumberField";
import { ManufacturerUrlField } from "./ItemDetailFields/ManufacturerUrlField";
import { QtyField } from "./ItemDetailFields/QtyField";
import { RatesFields } from "./ItemDetailFields/RatesFields";
import { ReplacementCostField } from "./ItemDetailFields/ReplacementCostField";
import { ShipWeightField } from "./ItemDetailFields/ShipWeightField";
import { TagsField } from "./ItemDetailFields/TagsField";
import { TechnicalNotesField } from "./ItemDetailFields/TechnicalNotesField";
import { ProductDetailsPackageItem as ProductDetailsPackageItemMobile } from "./PackageItem/Mobile/ProductDetailsPackageItemMobile";
import { ProductDetailsButtonBar } from "./ProductDetailsButtonBar";

//------------------------------------------------------------------------------------
export class ProductDetailsViewSettings {
	//hasImage: boolean = false;
	hasDepartmentCategorySubcategoryLinks: boolean = false;
	hasICode: boolean = false;
	//hasDescription: boolean = false;
	hasBarcodes: boolean = false;
	hasTags: boolean = false;
	hasDailyRate: boolean = false;
	hasWeeklyRate: boolean = false;
	hasMonthlyRate: boolean = false;
	hasQty: boolean = false;
	hasRates: boolean = false;
	hasAvailability: boolean = false;
	hasDetailedDescription: boolean = false;
	hasTechnicalNotes: boolean = false;
	allowAddingUnavailableItems: boolean = false;
}
export const getProductDetailsViewSettings = (
	catalogs?: ImmutableArray<Catalog>,
	product?: ImmutableObject<Product>,
): ProductDetailsViewSettings => {
	const viewSettings = new ProductDetailsViewSettings();
	if (catalogs && product) {
		const catalog = catalogs.find((c) => c.Id === product.WebCatalogId);
		if (catalog) {
			const itemDetailFieldsByName = catalog.itemDetailFieldsByName;
			if (itemDetailFieldsByName) {
				//viewSttings.hasImage = (shoppingCartFieldsByName['IMAGE'] !== undefined) && (shoppingCartFieldsByName['IMAGE'].Selected);
				viewSettings.hasDepartmentCategorySubcategoryLinks =
					itemDetailFieldsByName.DEPARTMENTCATEGORYSUBCATEGORYLINKS?.Selected;
				viewSettings.hasICode = itemDetailFieldsByName.ICODE?.Selected;
				//viewSettings.hasDescription = (shoppingCartFieldsByName['DESCRIPTION'] !== undefined) && (shoppingCartFieldsByName['DESCRIPTION'].Selected);
				viewSettings.hasBarcodes =
					itemDetailFieldsByName.BARCODENUMBER?.Selected;
				viewSettings.hasTags = itemDetailFieldsByName.TAGS?.Selected;
				viewSettings.hasQty =
					itemDetailFieldsByName.ITEMQUANTITYINPUT?.Selected;
				viewSettings.hasDailyRate = itemDetailFieldsByName.DAILYRATE?.Selected;
				viewSettings.hasWeeklyRate =
					itemDetailFieldsByName.WEEKLYRATE?.Selected;
				viewSettings.hasMonthlyRate =
					itemDetailFieldsByName.MONTHLYRATE?.Selected;
				viewSettings.hasRates =
					viewSettings.hasDailyRate ||
					viewSettings.hasWeeklyRate ||
					viewSettings.hasMonthlyRate;
				viewSettings.hasAvailability =
					itemDetailFieldsByName.AVAILABILITY?.Selected &&
					product.Availability !== undefined;
				viewSettings.hasDetailedDescription =
					itemDetailFieldsByName.DETAILEDDESCRIPTION?.Selected;
				viewSettings.hasTechnicalNotes =
					itemDetailFieldsByName.TECHNICALNOTES?.Selected;
			}
			viewSettings.allowAddingUnavailableItems =
				catalog.AllowAddingUnavailableItems;
		}
	}
	return viewSettings;
};

type ProductDetailsLoadedProps = {
	printMode: boolean;
};

const COLLAPSED_METADATA_HEIGHT = 288;

type ExpandableProductMetadataProps = React.PropsWithChildren<{
	defaultExpanded?: boolean;
}>;

const ExpandableProductMetadata: React.FC<ExpandableProductMetadataProps> = ({
	children,
	defaultExpanded = false,
}) => {
	const translate = useTranslate();
	const contentRef = useRef<HTMLDivElement>(null);
	const [expanded, setExpanded] = useState(defaultExpanded);
	const [hasOverflow, setHasOverflow] = useState(false);

	useEffect(() => {
		const content = contentRef.current;
		if (!content) {
			return undefined;
		}

		const updateOverflow = () => {
			setHasOverflow(content.scrollHeight > COLLAPSED_METADATA_HEIGHT + 1);
		};
		updateOverflow();

		const observer = new ResizeObserver(updateOverflow);
		observer.observe(content);
		return () => observer.disconnect();
	}, []);

	return (
		<div
			data-product-metadata-panel
			css={css`
				position: relative;
				margin-top: 1rem;
			`}
		>
			<div
				ref={contentRef}
				data-product-metadata-content
				css={css`
					position: relative;
					max-height: ${expanded ? "none" : `${COLLAPSED_METADATA_HEIGHT}px`};
					overflow: hidden;
					padding: 0 0.15rem;
					@media print {
						max-height: none;
						overflow: visible;
					}
					.field:last-child {
						padding-bottom: 0;
					}
				`}
			>
				{children}
				{hasOverflow && !expanded && (
					<div
						css={css`
							position: absolute;
							left: 0;
							right: 0;
							bottom: 0;
							height: 3.5rem;
							pointer-events: none;
							background: linear-gradient(
								to bottom,
								transparent,
								var(--background)
							);
						`}
					/>
				)}
			</div>
			{hasOverflow && (
				<Button
					type="button"
					variant="ghost"
					size="sm"
					aria-expanded={expanded}
					data-product-metadata-toggle
					onClick={() => setExpanded((value) => !value)}
					className="noprint mx-auto mt-1 flex h-8 rounded-md px-3 text-xs text-muted-foreground"
				>
					{expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
					{translate(expanded ? "Collapse" : "Expand")}
				</Button>
			)}
		</div>
	);
};

const ProductDetailsLoaded: React.FC<ProductDetailsLoadedProps> = (props) => {
	const productDetailsState = useProductDetailsState();
	const translate = useTranslate();
	const product = productDetailsState.get().product;
	const images = product?.Images ?? [];

	const catalogState = useCatalogState();
	const catalogs = catalogState.get().catalogs;
	const viewSettings = getProductDetailsViewSettings(catalogs, product);
	const hasImage = true;
	let hasDepartmentCategorySubcategoryLinks = false;
	let hasICode = false;
	const hasDescription = true;
	let hasPackageItems = false;
	let hasBarcodes = false;
	let hasTags = false;
	let hasDailyRate = false;
	let hasWeeklyRate = false;
	let hasMonthlyRate = false;
	let hasQty = false;
	let hasRates = false;
	let hasAvailability = false;
	let hasDetailedDescription = false;
	let hasTechnicalNotes = false;
	let allowAddingUnavailableItems = false;
	let hasManufacturer = false;
	let hasManufacturerPartNumber = false;
	let hasManufacturerUrl = false;
	let hasReplacementCost = false;
	let hasShipWeight = false;
	const packageItems = product?.PackageItems ?? [];
	if (catalogs && product) {
		const catalog = catalogs.find((c) => c.Id === product.WebCatalogId);
		if (catalog) {
			const itemDetailFieldsByName = catalog.itemDetailFieldsByName;
			if (itemDetailFieldsByName) {
				//hasImageField = (shoppingCartFieldsByName['IMAGE'] !== undefined) && (shoppingCartFieldsByName['IMAGE'].Selected);
				hasDepartmentCategorySubcategoryLinks =
					itemDetailFieldsByName.DEPARTMENTCATEGORYSUBCATEGORYLINKS?.Selected;
				hasICode = itemDetailFieldsByName.ICODE?.Selected;
				//hasDescription = (shoppingCartFieldsByName['DESCRIPTION'] !== undefined) && (shoppingCartFieldsByName['DESCRIPTION'].Selected);
				hasPackageItems =
					product.PackageItems !== undefined && product.PackageItems.length > 0;
				hasBarcodes = itemDetailFieldsByName.BARCODENUMBER?.Selected;
				hasTags = itemDetailFieldsByName.TAGS?.Selected;
				hasQty = itemDetailFieldsByName.ITEMQUANTITYINPUT?.Selected;
				hasDailyRate = itemDetailFieldsByName.DAILYRATE?.Selected;
				hasWeeklyRate = itemDetailFieldsByName.WEEKLYRATE?.Selected;
				hasMonthlyRate = itemDetailFieldsByName.MONTHLYRATE?.Selected;
				hasRates = hasDailyRate || hasWeeklyRate || hasMonthlyRate;
				hasAvailability =
					itemDetailFieldsByName.AVAILABILITY?.Selected &&
					product.Availability !== undefined;
				hasDetailedDescription =
					itemDetailFieldsByName.DETAILEDDESCRIPTION?.Selected;
				hasTechnicalNotes = itemDetailFieldsByName.TECHNICALNOTES?.Selected;

				hasManufacturer =
					itemDetailFieldsByName.MANUFACTURER?.Selected &&
					product.Manufacturer !== undefined &&
					product.Manufacturer.length > 0;
				hasManufacturerPartNumber =
					itemDetailFieldsByName.MANUFACTURERPARTNUMBER?.Selected &&
					product.ManufacturerPartNumber !== undefined &&
					product.ManufacturerPartNumber.length > 0;
				hasManufacturerUrl =
					itemDetailFieldsByName.MANUFACTURERURL?.Selected &&
					product.ManufacturerUrl !== undefined &&
					product.ManufacturerUrl.length > 0;
				hasReplacementCost =
					itemDetailFieldsByName.REPLACEMENTCOST?.Selected &&
					product.ReplacementCost !== undefined &&
					product.ReplacementCost > 0;
				const isShipWeightAvailable =
					product.ShipWeightLbs || product.ShipWeightOz;
				if (isShipWeightAvailable) {
					hasShipWeight = itemDetailFieldsByName.SHIPWEIGHT?.Selected;
				}
			}
			allowAddingUnavailableItems = catalog.AllowAddingUnavailableItems;
		}
	}
	const hasProductMetadata =
		(hasDetailedDescription && Boolean(product?.DetailedDescription)) ||
		(hasTechnicalNotes && Boolean(product?.TechnicalNotes)) ||
		(hasBarcodes && Boolean(product?.Barcodes)) ||
		(hasTags && Boolean(product?.Tags)) ||
		hasManufacturer ||
		hasManufacturerPartNumber ||
		hasManufacturerUrl ||
		hasReplacementCost ||
		hasShipWeight;
	const [isDesktop, setIsDesktop] = useState<boolean>(() => {
		if (typeof window === "undefined") {
			return false;
		}
		return window.matchMedia("(min-width: 929px)").matches;
	});
	useEffect(() => {
		if (typeof window === "undefined") {
			return undefined;
		}
		const mediaQuery = window.matchMedia("(min-width: 929px)");
		const updateIsDesktop = () => {
			setIsDesktop(mediaQuery.matches);
		};
		updateIsDesktop();
		mediaQuery.addEventListener("change", updateIsDesktop);
		return () => mediaQuery.removeEventListener("change", updateIsDesktop);
	}, []);

	const renderProductMetadata = () =>
		hasProductMetadata ? (
			<ExpandableProductMetadata defaultExpanded={!hasPackageItems}>
				{hasDetailedDescription && <DetailedDescriptionField />}
				{hasTechnicalNotes && <TechnicalNotesField />}
				{hasBarcodes && <BarcodesField />}
				{hasTags && <TagsField />}
				{hasManufacturer && <ManufacturerField />}
				{hasManufacturerPartNumber && <ManufacturerPartNumberField />}
				{hasManufacturerUrl && <ManufacturerUrlField />}
				{hasReplacementCost && <ReplacementCostField />}
				{hasShipWeight && <ShipWeightField />}
			</ExpandableProductMetadata>
		) : null;

	return (
		<div
			css={css`
            flex: 1 1 0;
            display:flex;
            justify-content:center;
            @media print {
                .noprint {display:none}
            }
            .field {
                /*display: flex;
                border-bottom: 1px dotted #aaaaaa;*/
                padding: .5em 0em;
            }
            .caption {
                flex: 0 0 6em;
                font-size:1rem;
                font-weight:bold;
                transition: color 1s;
                padding: .5rem 0;
            }
            .value {
                padding:0 0 0 .25em;
                font-size:1em;
                flex: 1 1 0;
            }
            .field.availability {
                font-size:.8rem;
                text-align: center;
            }
			`}
		>
			{!product && (
				<div
					css={css`
                    flex: 1 1 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: auto;`}
				>
					<LoaderCircle className="size-8 animate-spin text-primary" />
				</div>
			)}
			{product && (
				<div css={css`flex:1 1 0;display:flex;flex-direction:column;`}>
					<div
						css={css`
                            flex: 0 0 auto;
                            display:flex;
                        `}
					>
						{!props.printMode && <ProductDetailsButtonBar />}
					</div>
					<div
						css={css`
                        @media screen {
                            flex: 1 1 0;
                            display: flex;
                            flex-wrap: wrap;
                            justify-content: center;
                        }
						padding: .75rem 0;
                        `}
					>
						<div
							css={css`
								flex: 0 1 420px;
								min-width: 0;
								max-width: 420px;
								padding: 0 .75rem;
								@media (max-width: 767px) {
									flex-basis: 100%;
									max-width: 100%;
								}
							`}
						>
							{!props.printMode && <ReservationDates className="max-w-160" />}
							{hasDepartmentCategorySubcategoryLinks && (
								<DepartmentCategorySubcategoryLinks />
							)}
							{hasImage && <ImageField />}
						</div>
						<div
							css={css`
								flex: 1 1 28rem;
								min-width: 0;
								max-width: 100%;
								@media (max-width: 767px) {
									flex-basis: 100%;
								}
							`}
						>
							<div css={css`display:flex;flex-wrap:wrap;`}>
								{isDesktop && (
									<>
										<div
											css={css`flex:1 1 0;min-width:0;max-width:100%;padding: 0 1rem;`}
										>
											{product.PackageItems &&
												product.PackageItems.length > 0 && (
													<div
														css={css`background-color:oklch(0.46 0.24 260.45);color:#ffffff;padding:2px 0;font-size:.7rem;z-index:2;display:flex;align-items:center;justify-content:center;border-radius:5px;`}
													>
														<Package size={12} />
														&nbsp;
														{translate("package")}
													</div>
												)}
											{hasDescription && <ItemNameField />}
											{hasICode && <ItemNoField />}
											{renderProductMetadata()}
											{hasPackageItems && (
												<div
													css={css`
														display: grid;
														gap: 0.75rem;
														padding-top: 0.75rem;
													`}
												>
													<div
														css={css`
															font-size: 0.9rem;
															font-weight: 700;
														`}
													>
														{translate("Package includes")}
													</div>
													{packageItems
														.filter((item) => !item.IsOption)
														.map((item) => (
															<ProductDetailsPackageItemMobile
																key={item.InventoryId}
																packageProduct={product}
																packageItem={item}
																allowAddingUnavailableItems={
																	allowAddingUnavailableItems
																}
																enableQty={hasQty}
																viewSettings={viewSettings}
															/>
														))}
													{packageItems.some((item) => item.IsOption) && (
														<>
															<div
																css={css`
																	padding-top: 0.25rem;
																	font-size: 0.9rem;
																	font-weight: 700;
																`}
															>
																{translate("Optional accessories")}
															</div>
															{packageItems
																.filter((item) => item.IsOption)
																.map((item) => (
																	<ProductDetailsPackageItemMobile
																		key={item.InventoryId}
																		packageProduct={product}
																		packageItem={item}
																		allowAddingUnavailableItems={
																			allowAddingUnavailableItems
																		}
																		enableQty={hasQty}
																		viewSettings={viewSettings}
																	/>
																))}
														</>
													)}
												</div>
											)}
										</div>
										<div
											css={css`
                                            flex: ${isDesktop ? "0 0 200px;" : "1 1 0"};
                                            border: 1px solid rgba(200,200,200,.4);
                                            border-radius: 13px;
                                            justify-content: center;
                                            display:none;
                                            @media screen {
                                                display: block;
                                            }
                                        `}
										>
											<div
												css={css`margin:0 auto;max-width:175px;padding:1rem .25rem .25rem .25rem;`}
											>
												{hasQty && (
													<QtyField
														allowAddingUnavailableItems={
															allowAddingUnavailableItems
														}
													/>
												)}
											</div>
											{hasAvailability && <AvailabilityField />}
											{hasRates && <RatesFields />}
										</div>
									</>
								)}
								{!isDesktop && (
									<div css={css`min-width:0;max-width:100%;padding: 0 .25rem;`}>
										<div css={css`flex:1 1 0;min-width:0;max-width:100%;`}>
											{packageItems.length > 0 && (
												<div
													css={css`background-color:oklch(0.46 0.24 260.45);color:#ffffff;padding:2px 0;font-size:.7rem;z-index:2;display:flex;align-items:center;justify-content:center;border-radius:5px;`}
												>
													<Package size={12} />
													&nbsp;
													{translate("package")}
												</div>
											)}
											{hasDescription && <ItemNameField />}
											{hasICode && <ItemNoField />}
											{renderProductMetadata()}
										</div>
										<div
											css={css`
                                            flex: 0 0 200px;
                                            border: 1px solid rgba(200,200,200,.4);
                                            border-radius: 13px;
                                            justify-content: center;
                                            display:none;
                                            @media screen {
                                                display: block;
                                            }
                                        `}
										>
											<div
												css={css`margin:0 auto;width:175px;padding:1rem .25rem .25rem .25rem;`}
											>
												{hasQty && (
													<QtyField
														allowAddingUnavailableItems={
															allowAddingUnavailableItems
														}
													/>
												)}
											</div>
											{hasAvailability && <AvailabilityField />}
											{hasRates && <RatesFields />}
										</div>
										{hasPackageItems && (
											<div
												css={css`
													display: grid;
													gap: 0.75rem;
													padding-top: 0.75rem;
												`}
											>
												<div
													css={css`
														font-size: 0.9rem;
														font-weight: 700;
													`}
												>
													{translate("Package includes")}
												</div>
												{packageItems
													.filter((item) => !item.IsOption)
													.map((item) => (
														<ProductDetailsPackageItemMobile
															key={item.InventoryId}
															packageProduct={product}
															packageItem={item}
															allowAddingUnavailableItems={
																allowAddingUnavailableItems
															}
															enableQty={hasQty}
															viewSettings={viewSettings}
														/>
													))}
												{packageItems.some((item) => item.IsOption) && (
													<>
														<div
															css={css`
																padding-top: 0.25rem;
																font-size: 0.9rem;
																font-weight: 700;
															`}
														>
															{translate("Optional accessories")}
														</div>
														{packageItems
															.filter((item) => item.IsOption)
															.map((item) => (
																<ProductDetailsPackageItemMobile
																	key={item.InventoryId}
																	packageProduct={product}
																	packageItem={item}
																	allowAddingUnavailableItems={
																		allowAddingUnavailableItems
																	}
																	enableQty={hasQty}
																	viewSettings={viewSettings}
																/>
															))}
													</>
												)}
											</div>
										)}
									</div>
								)}
							</div>
						</div>
					</div>

					<div
						css={css`
                            display:none;
                            @media print {
                                display: flex;
                                flex-direction: column;
                                justify-content:center;
                            }
                            `}
					>
						{images.map((image, index) => {
							if (index > 0) {
								return (
									<div
										key={image.Id}
										css={css`
                                            display:flex;
                                            align-items:center;
                                            justify-content:center;
                                            flex-wrap: wrap;
                                            `}
									>
										<img
											src={image.Url}
											alt=""
											style={{
												maxWidth: `128px`,
											}}
										/>
									</div>
								);
							}
							return null;
						})}
					</div>
				</div>
			)}
		</div>
	);
};
//------------------------------------------------------------------------------------
export type ProductDetailsProps = {
	printMode: boolean;
};
export const ProductDetails: React.FC<ProductDetailsProps> = (props) => {
	const productDetailsState = useProductDetailsState();
	const product = productDetailsState.get().product;

	useEffect(() => {
		document.title = product?.ItemName ?? "Product Details";
	}, [product?.ItemName]);

	return (
		<div
			css={css`
                display:flex;
                flex-align:flex-start;
                justify-content:center;
                flex: 1 1 0;
                padding:1em;`}
		>
			{!product && (
				<div
					css={css`
                        flex:1 1 0;
                        display: flex;
                        align-items: center;
                        justify-content: center;`}
				>
					<LoaderCircle className="size-8 animate-spin text-primary" />
				</div>
			)}
			{product && <ProductDetailsLoaded printMode={props.printMode} />}
		</div>
	);
};

export type ProductDetailsPopupProps = {
	layoutId: string;
};
export const ProductDetailsPopup: React.FC<ProductDetailsPopupProps> = (
	props,
) => {
	const productDetailsState = useProductDetailsState();
	const themeState = useThemeState();
	const themeName = themeState.get().themeName;
	const product = productDetailsState.get().product;

	return (
		<AnimatePresence>
			{productDetailsState.get().showPopup && product && (
				// biome-ignore lint/a11y/noStaticElementInteractions: the implications of changing this were unclear
				<div
					css={css`
                        position:fixed;
                        top:0;
                        left:0;
                        right:0;
                        bottom:0;
                        z-index:101;
                        flex:1;
                        display:flex;
                        `}
					onClick={async () => {
						return Promise.resolve().then(() => {
							if (productDetailsState.get().showPopup) {
								productDetailsState.setShowPopup(false);
							}
						});
					}}
				>
					<motion.div
						css={css`
                            flex:1;
                            z-index: 2;
                            background-color: ${themeName === "dark" ? "rgb(33,33,33)" : "#ffffff"};
                            display:flex;
                        `}
						layoutId={props.layoutId}
						transition={{ duration: 0.3, type: "tween" }}
						//transition={{ type: "spring", stiffness: 500, damping: 70 }}
					>
						<CustomScrollbars>
							<div
								css={css`
                                    flex:1 1 0;
                                    display:flex;
                                    flex-direction:column;`}
								// onClick={async (e) => {
								// 	e.stopPropagation();
								// }}
							>
								<ProductDetails
									key={productDetailsState.get().product?.Id ?? "0"}
									printMode={false}
								/>
							</div>
						</CustomScrollbars>
					</motion.div>
				</div>
			)}
		</AnimatePresence>
	);
};
//------------------------------------------------------------------------------------
